import React, { useEffect, useState } from "react";
import {
    Mail,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Save,
    Send,
    Info,
    X,
    Plus,
} from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import HowThisWorks from "@/components/admin/HowThisWorks";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsLeadEmail() {
    const [form, setForm] = useState({
        lead_email_enabled: false,
        smtp_host: "",
        smtp_port: 587,
        smtp_user: "",
        smtp_pass: "",
        smtp_from: "",
        smtp_from_name: "Orchid Concierge",
        smtp_use_tls: true,
        smtp_use_ssl: false,
        lead_recipients: [],
        missed_chat_alert_enabled: true,
    });
    const [meta, setMeta] = useState({
        configured: false,
        smtp_pass_set: false,
        smtp_pass_last4: "",
        last_test_at: null,
        last_test_status: null,
        last_test_message: null,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [newRecipient, setNewRecipient] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/api/admin/settings/email-smtp");
            setForm({
                lead_email_enabled: data.lead_email_enabled,
                smtp_host: data.smtp_host || "",
                smtp_port: data.smtp_port || 587,
                smtp_user: data.smtp_user || "",
                smtp_pass: "",
                smtp_from: data.smtp_from || "",
                smtp_from_name: data.smtp_from_name || "Orchid Concierge",
                smtp_use_tls: data.smtp_use_tls,
                smtp_use_ssl: data.smtp_use_ssl,
                lead_recipients: data.lead_recipients || [],
                missed_chat_alert_enabled: data.missed_chat_alert_enabled !== false,
            });
            setMeta({
                configured: data.configured,
                smtp_pass_set: data.smtp_pass_set,
                smtp_pass_last4: data.smtp_pass_last4 || "",
                last_test_at: data.last_test_at,
                last_test_status: data.last_test_status,
                last_test_message: data.last_test_message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const addRecipient = () => {
        const e = newRecipient.trim().toLowerCase();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
            toast.error("Please enter a valid email address");
            return;
        }
        if (form.lead_recipients.includes(e)) {
            toast.error("That email is already added");
            setNewRecipient("");
            return;
        }
        setForm((f) => ({ ...f, lead_recipients: [...f.lead_recipients, e] }));
        setNewRecipient("");
    };

    const removeRecipient = (e) =>
        setForm((f) => ({ ...f, lead_recipients: f.lead_recipients.filter((x) => x !== e) }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                lead_email_enabled: form.lead_email_enabled,
                smtp_host: form.smtp_host.trim(),
                smtp_port: Number(form.smtp_port) || 587,
                smtp_user: form.smtp_user.trim(),
                smtp_from: form.smtp_from.trim(),
                smtp_from_name: form.smtp_from_name.trim() || "Orchid Concierge",
                smtp_use_tls: form.smtp_use_tls,
                smtp_use_ssl: form.smtp_use_ssl,
                lead_recipients: form.lead_recipients,
                missed_chat_alert_enabled: form.missed_chat_alert_enabled,
            };
            if (form.smtp_pass.trim()) payload.smtp_pass = form.smtp_pass.trim();
            await adminApi.put("/api/admin/settings/email-smtp", payload);
            toast.success("Email settings saved");
            setForm((f) => ({ ...f, smtp_pass: "" }));
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        try {
            const { data } = await adminApi.post("/api/admin/settings/email-smtp/test", {
                to: testTo.trim() || undefined,
            });
            toast.success(`Test email sent to ${(data.sent_to || []).join(", ")}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test failed");
            load();
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-10 text-stone-500 flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Loading email settings…
            </div>
        );
    }

    const inputCls =
        "w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15";

    return (
        <div className="p-10 max-w-3xl" data-testid="page-settings-lead-email">
            <PageHeader
                eyebrow="Settings"
                title="Lead Emails (SMTP)"
                description="Every booking, wedding, membership and support lead Priya captures is saved to the admin Leads page AND emailed instantly to your team via your SMTP server."
            />

            {/* Status banner */}
            <div
                className={`mb-6 border rounded-lg p-4 flex items-start gap-3 ${
                    meta.configured && form.lead_email_enabled
                        ? meta.last_test_status === "sent"
                            ? "border-sage-500/40 bg-sage-500/5"
                            : "border-stone-200 bg-stone-50"
                        : "border-gold-500/40 bg-gold-500/5"
                }`}
                data-testid="email-status"
            >
                {meta.configured && form.lead_email_enabled ? (
                    meta.last_test_status === "sent" ? (
                        <CheckCircle2 className="text-sage-700 mt-0.5 shrink-0" size={18} />
                    ) : (
                        <Info className="text-stone-500 mt-0.5 shrink-0" size={18} />
                    )
                ) : (
                    <AlertCircle className="text-gold-700 mt-0.5 shrink-0" size={18} />
                )}
                <div className="flex-1 text-sm">
                    {!form.lead_email_enabled ? (
                        <>
                            <div className="font-medium text-stone-900">
                                Lead emails are turned OFF.
                            </div>
                            <div className="text-stone-600 mt-0.5">
                                Leads are still saved to the admin Leads page, but no email is sent.
                                Turn on the toggle below and save.
                            </div>
                        </>
                    ) : !meta.configured ? (
                        <>
                            <div className="font-medium text-stone-900">
                                Almost there — add SMTP host, username, password and at least one recipient.
                            </div>
                            {meta.last_test_message && (
                                <div className="text-stone-600 mt-0.5">{meta.last_test_message}</div>
                            )}
                        </>
                    ) : meta.last_test_status === "sent" ? (
                        <>
                            <div className="font-medium text-sage-700">
                                Configured — test email delivered successfully.
                            </div>
                            <div className="text-stone-600 mt-0.5">{meta.last_test_message}</div>
                        </>
                    ) : (
                        <>
                            <div className="font-medium text-stone-900">
                                Configured — send a test email to confirm delivery.
                            </div>
                            {meta.last_test_message && (
                                <div className="text-stone-600 mt-0.5">{meta.last_test_message}</div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Enable toggle */}
            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6 flex items-center justify-between">
                <div>
                    <div className="font-medium text-stone-900">Email me every new lead</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                        Sends an instant email to your recipients whenever a lead is captured.
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={form.lead_email_enabled}
                    data-testid="lead-email-toggle"
                    onClick={() =>
                        setForm((f) => ({ ...f, lead_email_enabled: !f.lead_email_enabled }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        form.lead_email_enabled ? "bg-orchid-700" : "bg-stone-300"
                    }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            form.lead_email_enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                </button>
            </div>

            {/* Missed-chat alert toggle */}
            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6 flex items-center justify-between">
                <div>
                    <div className="font-medium text-stone-900">Alert me when no live agent is online</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                        Emails your team whenever a guest asks for a live agent and none is available, so no request is missed.
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={form.missed_chat_alert_enabled}
                    data-testid="missed-chat-toggle"
                    onClick={() =>
                        setForm((f) => ({ ...f, missed_chat_alert_enabled: !f.missed_chat_alert_enabled }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        form.missed_chat_alert_enabled ? "bg-orchid-700" : "bg-stone-300"
                    }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            form.missed_chat_alert_enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                </button>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6">
                <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                    <Mail size={16} className="text-orchid-700" /> SMTP server
                </h3>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-2">
                            <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                SMTP host
                            </label>
                            <input
                                value={form.smtp_host}
                                onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                                placeholder="smtp.gmail.com"
                                data-testid="smtp-host"
                                className={`${inputCls} font-mono`}
                            />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                Port
                            </label>
                            <input
                                type="number"
                                value={form.smtp_port}
                                onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                                placeholder="587"
                                data-testid="smtp-port"
                                className={`${inputCls} font-mono`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            Username
                        </label>
                        <input
                            value={form.smtp_user}
                            onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
                            placeholder="notifications@orchidhotel.com"
                            data-testid="smtp-user"
                            className={`${inputCls} font-mono`}
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            Password{" "}
                            {meta.smtp_pass_set && (
                                <span className="text-stone-400 normal-case tracking-normal">
                                    (currently ••••{meta.smtp_pass_last4})
                                </span>
                            )}
                        </label>
                        <input
                            type="password"
                            value={form.smtp_pass}
                            onChange={(e) => setForm({ ...form, smtp_pass: e.target.value })}
                            placeholder={
                                meta.smtp_pass_set ? "Leave blank to keep current password" : "SMTP / app password"
                            }
                            data-testid="smtp-pass"
                            className={`${inputCls} font-mono`}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                From address
                            </label>
                            <input
                                value={form.smtp_from}
                                onChange={(e) => setForm({ ...form, smtp_from: e.target.value })}
                                placeholder="Defaults to username"
                                data-testid="smtp-from"
                                className={`${inputCls} font-mono`}
                            />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                                From name
                            </label>
                            <input
                                value={form.smtp_from_name}
                                onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })}
                                placeholder="Orchid Concierge"
                                data-testid="smtp-from-name"
                                className={inputCls}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-sm text-stone-700">
                            <input
                                type="checkbox"
                                checked={form.smtp_use_tls}
                                onChange={(e) =>
                                    setForm({ ...form, smtp_use_tls: e.target.checked, smtp_use_ssl: false })
                                }
                                data-testid="smtp-tls"
                            />
                            STARTTLS (port 587)
                        </label>
                        <label className="flex items-center gap-2 text-sm text-stone-700">
                            <input
                                type="checkbox"
                                checked={form.smtp_use_ssl}
                                onChange={(e) =>
                                    setForm({ ...form, smtp_use_ssl: e.target.checked, smtp_use_tls: false })
                                }
                                data-testid="smtp-ssl"
                            />
                            SSL (port 465)
                        </label>
                    </div>
                    <div data-testid="lead-recipients">
                        <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1.5 block">
                            Send leads to
                        </label>
                        <p className="text-xs text-stone-500 mb-2">
                            Add as many recipients as you like (e.g. your reservations inbox and your CRM
                            address). Every captured lead is emailed to all of them.
                        </p>
                        {form.lead_recipients.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2" data-testid="lead-recipients-list">
                                {form.lead_recipients.map((e) => (
                                    <span
                                        key={e}
                                        data-testid={`recipient-chip-${e}`}
                                        className="inline-flex items-center gap-1.5 bg-orchid-700/10 text-orchid-800 border border-orchid-700/20 rounded-full pl-3 pr-1.5 py-1 text-sm"
                                    >
                                        {e}
                                        <button
                                            type="button"
                                            onClick={() => removeRecipient(e)}
                                            data-testid={`recipient-remove-${e}`}
                                            aria-label={`Remove ${e}`}
                                            className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-orchid-700/20 text-orchid-700"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <input
                                value={newRecipient}
                                onChange={(e) => setNewRecipient(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                        e.preventDefault();
                                        addRecipient();
                                    }
                                }}
                                placeholder="Add an email and press Enter"
                                type="email"
                                data-testid="lead-recipient-input"
                                className={inputCls}
                            />
                            <button
                                type="button"
                                onClick={addRecipient}
                                data-testid="lead-recipient-add"
                                className="inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium px-4 py-2 rounded-md whitespace-nowrap"
                            >
                                <Plus size={14} /> Add
                            </button>
                        </div>
                        <p className="text-[11px] text-stone-400 mt-1.5">
                            Tip: click <strong>Save settings</strong> below after adding or removing recipients.
                        </p>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="email-save"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        {saving ? "Saving…" : "Save settings"}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-6 mb-8">
                <h3 className="font-medium text-stone-900 mb-1 flex items-center gap-2">
                    <Send size={16} className="text-orchid-700" /> Send a test email
                </h3>
                <p className="text-xs text-stone-500 mb-4">
                    Verifies the connection and sends a sample lead email. Save your settings first.
                </p>
                <div className="flex gap-2">
                    <input
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                        placeholder="Test recipient (optional — defaults to your recipients)"
                        data-testid="email-test-to"
                        className={inputCls}
                    />
                    <button
                        onClick={test}
                        disabled={testing}
                        data-testid="email-test"
                        className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium px-5 py-2 rounded-md disabled:opacity-50 whitespace-nowrap"
                    >
                        {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        {testing ? "Sending…" : "Send test"}
                    </button>
                </div>
            </div>

            <HowThisWorks
                steps={[
                    "Enter your SMTP host, port, username and password (e.g. from Gmail, Zoho, Office365 or your hosting provider) and the recipient email(s).",
                    "Choose STARTTLS (port 587) or SSL (port 465) to match your provider.",
                    "Turn on 'Email me every new lead' and Save.",
                    "Click Send test to confirm delivery — then every captured lead arrives in your inbox automatically (and stays on the Leads page).",
                ]}
            />
        </div>
    );
}
